import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';
import {Link as RoutedLink} from 'react-router-dom';

import {SaveButton} from 'shells/button/save-button/save-button';
import {Link} from 'shells/link/link';
import {RightFrame, ModalMain, NavButtons} from 'shells/modal/step-modal/layout';
import {ModalHeader} from 'shells/typography/modal-header';

import {CallrailForm} from './callrail-form';
import {CallrailUpdateSigningKey} from './callrail-update-signing-key';
import {useCreateCallRailIntegration} from './graphql/hooks/use-create-call-rail-integration';
import {useSetCallRailIntegrationIsEnabled} from './graphql/hooks/use-set-call-rail-integration-is-enabled';
import {useUpdateCallRailIntegration} from './graphql/hooks/use-update-call-rail-integration';

type Props = {
    prevPage: string;
    history: RouterHistory;
};

export function CallrailSetup(props: Props) {
    const [needsSigningKey, setNeedsSigningKey] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const [apiKey, setApiKey] = React.useState<string>('');
    const [accountId, setAccountId] = React.useState<string>('');
    const [companyId, setCompanyId] = React.useState<string>('');
    const [callrailEmail, setCallrailEmail] = React.useState<string>('');
    const [signingKey, setSigningKey] = React.useState<string>('');

    const hasFormValues = Boolean(apiKey && accountId && companyId && callrailEmail);

    const {createCallRailIntegration, error: errorCreating} = useCreateCallRailIntegration();
    const {updateCallRailIntegration, error: errorUpdating} = useUpdateCallRailIntegration();
    const {setCallRailIntegrationIsEnabled, error: errorEnabling} =
        useSetCallRailIntegrationIsEnabled();

    const onSubmit = () => {
        if (!hasFormValues) return;
        setIsSaving(true);
        createCallRailIntegration({apiKey, accountId, companyId, callrailEmail})
            .then(({data}) => {
                if (
                    data &&
                    data.callRailIntegrationCreate &&
                    (data.callRailIntegrationCreate.status === 'WEBHOOK_INTEGRATION_CREATED' ||
                        data.callRailIntegrationCreate.status === 'VALID_INTEGRATION_EXISTS')
                ) {
                    onSetCallRailIntegrationIsEnabled();
                } else if (
                    data &&
                    data.callRailIntegrationCreate &&
                    data.callRailIntegrationCreate.status === 'NEEDS_SIGNING_KEY'
                ) {
                    setNeedsSigningKey(true);
                    setIsSaving(false);
                }
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    const onUpdateSigningKey = () => {
        if (!signingKey) return;
        setIsSaving(true);
        updateCallRailIntegration({signingKey})
            .then(({data}) => {
                if (data && data.callRailIntegrationUpdate) {
                    onSetCallRailIntegrationIsEnabled();
                }
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    const onSetCallRailIntegrationIsEnabled = () => {
        setIsSaving(true);
        setCallRailIntegrationIsEnabled({isEnabled: true})
            .then(({data}) => {
                if (data && data.setCallRailIntegrationIsEnabled) {
                    setIsSaving(false);
                    props.history.push(
                        '/connect-phone-integration/sync-preferences?integrationType=CALLRAIL'
                    );
                }
            })
            .catch(() => {
                setIsSaving(false);
            });
    };

    return (
        <RightFrame>
            <ModalMain fullheight={true}>
                <ModalHeader>Set up your CallRail integration</ModalHeader>
                {needsSigningKey ? (
                    <CallrailUpdateSigningKey
                        signingKey={signingKey}
                        setSigningKey={setSigningKey}
                    />
                ) : (
                    <CallrailForm
                        apiKey={apiKey}
                        accountId={accountId}
                        companyId={companyId}
                        callrailEmail={callrailEmail}
                        setApiKey={setApiKey}
                        setAccountId={setAccountId}
                        setCompanyId={setCompanyId}
                        setCallrailEmail={setCallrailEmail}
                    />
                )}
            </ModalMain>
            <NavButtons>
                {/* @ts-expect-error */}
                <Link as={RoutedLink} to={props.prevPage}>
                    Back
                </Link>
                <SaveButton
                    variant='primary'
                    size='big'
                    disabled={!hasFormValues}
                    // @ts-expect-error
                    onClick={needsSigningKey ? onUpdateSigningKey : onSubmit}
                    isSaving={isSaving}
                    buttonText='Next'
                    resetOnComplete={true}
                    errorSaving={Boolean(errorCreating || errorEnabling || errorUpdating)}
                />
            </NavButtons>
        </RightFrame>
    );
}
